import { ChunkData, SceneId, ServerMessagePayload, TilesetData } from '@common/api';
import { Graphics } from 'pixi.js';

import { ClientGlobalSystem } from './ClientBase';

const BLOCKED_COLOR = 0xff0000;
const BLOCKED_ALPHA = 0.4;

/**
 * Debug system that renders collision tiles overlay.
 * Shows blocked tiles (red for tile collisions, green for object collisions).
 * Toggled via the Debug Panel.
 */
export class DebugCollisionRenderSystem extends ClientGlobalSystem {
  private graphics: Graphics | null = null;
  private initialized = false;
  private lastShowState = false;
  private lastSceneId: SceneId | undefined;
  private lastChunkKeys = '';
  private pendingRedraw = false;

  run(): ServerMessagePayload[] {
    const world = this.getWorld();
    const chunkDataService = this.clientServices.chunkData;
    const eventBus = this.clientServices.common.eventBus;
    const showCollisionGrid = this.clientServices.debug.state.showCollisionGrid;

    // Handle visibility toggle
    if (this.graphics) {
      this.graphics.visible = showCollisionGrid;
    }

    // Early exit if nothing to show
    if (!showCollisionGrid) {
      this.resetRenderCache();
      return [];
    }

    // Get local player's scene
    const localEntityId = this.clientServices.localPlayer.entityId;
    const localPlayerTransform = localEntityId
      ? world.getComponent(localEntityId, 'Transform')
      : null;
    const currentSceneId = localPlayerTransform?.sceneId;
    if (!currentSceneId) {
      this.pendingRedraw = true;
      return [];
    }

    const currentChunkKeys = this.getChunkKeys(currentSceneId);
    const sceneChangedEvents = eventBus.getEventsOfType('SceneChanged');
    const shouldInvalidate =
      !this.initialized ||
      this.lastShowState !== showCollisionGrid ||
      this.lastSceneId !== currentSceneId ||
      this.lastChunkKeys !== currentChunkKeys ||
      sceneChangedEvents.length > 0;

    if (shouldInvalidate) {
      this.pendingRedraw = true;
    }

    // Get scene metadata and chunks
    const metadata = chunkDataService.getSceneMetadata(currentSceneId);
    const chunks = chunkDataService.getChunks(currentSceneId);
    if (!metadata || !chunks) {
      return [];
    }

    // Create graphics if not exists
    if (!this.graphics) {
      this.graphics = new Graphics();
      this.graphics.zIndex = 100000;
      this.clientServices.render.stage?.addChild(this.graphics);
    }

    const needsRedraw = this.pendingRedraw;
    if (!needsRedraw) return [];

    this.initialized = true;
    this.lastShowState = showCollisionGrid;
    this.lastSceneId = currentSceneId;
    this.lastChunkKeys = currentChunkKeys;
    this.pendingRedraw = false;

    const CHUNK_SIZE = metadata.chunkSize;
    const tilesets = metadata.tilesets;
    const tileWidth = metadata.tileWidth;
    const tileHeight = metadata.tileHeight;

    this.graphics.clear();

    for (const [, chunkData] of chunks) {
      this.renderCollisions(this.graphics, chunkData, tilesets, CHUNK_SIZE, tileWidth, tileHeight);
    }

    return [];
  }

  private resetRenderCache(): void {
    this.initialized = false;
    this.lastShowState = false;
    this.lastSceneId = undefined;
    this.lastChunkKeys = '';
    this.pendingRedraw = true;
  }

  private getChunkKeys(sceneId: SceneId): string {
    const chunks = this.clientServices.chunkData.getChunks(sceneId);
    if (!chunks) {
      return '';
    }

    return Array.from(chunks.keys()).sort().join(',');
  }

  private renderCollisions(
    graphics: Graphics,
    chunkData: ChunkData,
    tilesets: TilesetData[],
    chunkSize: number,
    tileWidth: number,
    tileHeight: number,
  ): void {
    const chunkWorldX = chunkData.chunkX * chunkSize * tileWidth;
    const chunkWorldY = chunkData.chunkY * chunkSize * tileHeight;

    // Check each tile for collision
    for (let localRow = 0; localRow < chunkSize; localRow++) {
      for (let localCol = 0; localCol < chunkSize; localCol++) {
        const localIndex = localRow * chunkSize + localCol;
        const worldX = chunkWorldX + localCol * tileWidth;
        const worldY = chunkWorldY + localRow * tileHeight;

        let hasCollision = false;
        for (const layerName in chunkData.layers) {
          const tileId = chunkData.layers[layerName][localIndex];
          if (tileId !== 0 && tileId !== undefined) {
            if (this.tileHasCollision(tileId, tilesets)) {
              hasCollision = true;
              break;
            }
          }
        }

        if (hasCollision) {
          graphics.rect(worldX, worldY, tileWidth, tileHeight);
          graphics.fill({ color: BLOCKED_COLOR, alpha: BLOCKED_ALPHA });
        }
      }
    }

    // Object AABB visualization derived from GID blocksWalking
    for (const obj of chunkData.objects ?? []) {
      if (!obj.gid) continue;

      const tileset = tilesets.find(
        (ts) => obj.gid! >= ts.firstgid && obj.gid! < ts.firstgid + ts.tilecount,
      );
      if (!tileset?.blocksWalking) continue;

      const localId = obj.gid - tileset.firstgid;
      if (!tileset.blocksWalking[localId]) continue;

      // Draw object bounds as a simple rect for now
      const objWorldX = obj.x;
      const objWorldY = obj.y - obj.height;
      graphics.rect(objWorldX, objWorldY, obj.width, obj.height);
      graphics.fill({ color: 0x00ff00, alpha: BLOCKED_ALPHA }); // Green for object collisions
    }
  }

  private tileHasCollision(tileId: number, tilesets: TilesetData[]): boolean {
    const tileset = tilesets.find(
      (ts) => tileId >= ts.firstgid && tileId < ts.firstgid + ts.tilecount,
    );
    if (!tileset?.blocksWalking) return false;
    const localId = tileId - tileset.firstgid;
    return !!tileset.blocksWalking[localId];
  }
}
